import { FC, useEffect, useRef } from 'react';

import { useNavigate } from 'react-router';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import LineNotificationPreference from 'assets/images/LineNotificationPreference.png';
import LineNotificationSlide1 from 'assets/images/LineNotificationSlide1.png';
import LineNotificationSlide2 from 'assets/images/LineNotificationSlide2.png';
import WANotificationPreference from 'assets/images/WANotificationPreference.png';
import WANotificationSlide1 from 'assets/images/WANotificationSlide1.png';
import WANotificationSlide2 from 'assets/images/WANotificationSlide2.png';
import { Button, Modal } from 'components';
import { pagePaths } from 'config/pages';
import { NOTIFICATION_PREFERENCE } from 'constants/common';
import { TRACK_EVENTS } from 'constants/segment';
import { LINE_ENABLED_COUNTRIES } from 'constants/settings';
import { useUpdateUserPreference } from 'services/hooks/TemplateSettings.hooks';
import { useIsmobile } from 'utils/customHooks/useViewport';
import { translate } from 'utils/locale';

import './styles.css';

type Props = {
  open: boolean;
  onClose: () => void;
  country: string;
  notificationPreferenceData: any;
};

const NotificationPreferenceIndicator: FC<Props> = ({
  open,
  onClose,
  country,
  notificationPreferenceData
}) => {
  const isMobile = useIsmobile();
  const hasTrackedEvent = useRef(false);

  const [updateUserPreference] = useUpdateUserPreference();
  const navigate = useNavigate();

  useEffect(() => {
    if (open && !hasTrackedEvent.current) {
      window.analytics?.track(TRACK_EVENTS.NOTIFICATION_POPUP_SHOWN, {
        popupViewCount: (notificationPreferenceData?.popupViewCount || 0) + 1
      });
      hasTrackedEvent.current = true;
    }
  }, [open, notificationPreferenceData?.popupViewCount]);

  const sliderSettings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    customPaging: () => (
      <div className='w-2 h-2 rounded-full bg-GRAY_300 mt-4 mb-4' />
    )
  };

  const isLineEnabledCountry = LINE_ENABLED_COUNTRIES.includes(country);

  const desktopNotificationPreferenceImage = isLineEnabledCountry
    ? LineNotificationPreference
    : WANotificationPreference;

  const getMobileSlides = () => {
    if (isLineEnabledCountry) {
      return [
        {
          image: LineNotificationSlide1,
          alt: 'Line notification preference indicator slide 1'
        },
        {
          image: LineNotificationSlide2,
          alt: 'Line notification preference indicator slide 2'
        }
      ];
    }
    return [
      {
        image: WANotificationSlide1,
        alt: 'WhatsApp notification preference indicator slide 1'
      },
      {
        image: WANotificationSlide2,
        alt: 'WhatsApp notification preference indicator slide 2'
      }
    ];
  };

  const handlePreferenceSet = () => {
    updateUserPreference({
      variables: {
        name: NOTIFICATION_PREFERENCE,
        preference: {
          popupViewCount: (notificationPreferenceData?.popupViewCount || 0) + 1,
          lastPopupViewDate: new Date().toISOString()
        }
      }
    });
  };

  const handleSkip = () => {
    handlePreferenceSet();
    onClose();
  };

  const handleSetPreference = () => {
    handleSkip();
    navigate(`${pagePaths.notificationPreferences}`);
  };

  return (
    <Modal
      open={open}
      onClose={handleSkip}
      showCloseIcon={false}
      classNames={{
        modalContainer: 'px-5',
        root: 'fixed'
      }}
    >
      <div className='flex flex-col items-center p-5 md:py-8 md:px-6'>
        <span className='text-sm text-BROWN bg-YELLOW rounded-4xl px-3 mb-2'>
          {translate('new.feature')}
        </span>
        <h1 className='text-17px md:text-2xl font-semibold mb-1 md:mb-2 self-start'>
          {translate('get.notification.on.patient.updates', {
            platform: isLineEnabledCountry
              ? translate('common.line')
              : translate('common.whatsapp')
          })}
        </h1>
        <span className='text-sm md:text-17px mb-7 self-start'>
          {translate('notify.you.where.you.are')}
        </span>
        {isMobile ? (
          <div className='w-full'>
            <Slider {...sliderSettings} className='w-full'>
              {getMobileSlides().map((slide, index) => (
                <div key={index}>
                  <img src={slide.image} alt={slide.alt} className='w-full' />
                </div>
              ))}
            </Slider>
          </div>
        ) : (
          <img
            src={desktopNotificationPreferenceImage}
            alt='notification preference indicator'
            className='w-[822px] h-[473px]'
          />
        )}
        <div className='flex md:flex-row md:self-end gap-4 mt-12 md:mt-0 flex-col-reverse w-full md:w-auto'>
          <Button
            className='bg-WHITE text-DEFAULT_TEXT px-6 py-3 text-sm'
            onClick={handleSkip}
          >
            {translate('skip_button.skip')}
          </Button>
          <Button
            className='bg-PRIMARY text-WHITE px-6 py-3 text-sm w-full'
            onClick={handleSetPreference}
          >
            {translate('set.preference')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default NotificationPreferenceIndicator;
